/**
 * ERP nomenklatúra szinkron napló szolgáltatás.
 *
 * Elszigetelt modul: a szinkron folyamat működését nem befolyásolja.
 *
 * A naplót elsődlegesen a backend állítja elő és tárolja adatbázisban
 * (GET ERP_SYNC_LOGS). A kliens csak akkor ír (POST ERP_SYNC_LOGS), ha a
 * backend ezt támogatja; ha az írás nem engedélyezett (405) vagy az endpoint
 * hiányzik (404/501), a bejegyzés lokális fallback tárolóba kerül, hogy a
 * kliens oldalon észlelt hibák se vesszenek el.
 *
 * Az írás és az olvasás elérhetősége külön kezelt: az írás tiltottsága
 * nem akadályozhatja a szerveren tárolt napló lekérdezését.
 */
import axios from "@/plugins/axios";
import * as endpoints from "@/store/api/endpoints";

const FALLBACK_STORAGE_KEY = "erp_sync_logs_fallback";
const FALLBACK_MAX_ITEMS = 500;
const MISSING_ENDPOINT_STATUSES = [404, 501];

let readAvailable = true;

export const ERP_SYNC_LOG_STATUS = {
  SUCCESS: "success",
  ERROR: "error",
  RUNNING: "running",
  OPRIT: "oprit",
};

function isMissingReadEndpoint(error) {
  const status = error?.response?.status;

  return !status || MISSING_ENDPOINT_STATUSES.includes(status);
}

function safeParse(raw) {
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

function readFallback() {
  if (typeof window === "undefined" || !window.localStorage) {
    return [];
  }

  return safeParse(window.localStorage.getItem(FALLBACK_STORAGE_KEY));
}

function writeFallback(items) {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }

  const trimmed = items.slice(0, FALLBACK_MAX_ITEMS);

  try {
    window.localStorage.setItem(FALLBACK_STORAGE_KEY, JSON.stringify(trimmed));
  } catch (e) {
    // Quota túllépés esetén csak a legutóbbi bejegyzéseket tartjuk meg.
    try {
      window.localStorage.setItem(
        FALLBACK_STORAGE_KEY,
        JSON.stringify(trimmed.slice(0, 50))
      );
    } catch (innerError) {
      // A napló mentése soha nem törheti meg a fő folyamatot.
    }
  }
}

function normalizeEntityCode(code) {
  return (code || "").toString().trim().toUpperCase();
}

function filterByEntityCode(items, entityCode) {
  const normalizedCode = normalizeEntityCode(entityCode);

  if (!normalizedCode) {
    return items;
  }

  return items.filter((item) => normalizeEntityCode(item?.entity_code) === normalizedCode);
}

/**
 * A trace érkezhet stringként vagy strukturált tömbként (Laravel).
 * Mindkettőt olvasható szöveggé alakítjuk.
 */
function normalizeTrace(trace) {
  if (!trace) {
    return "";
  }

  if (typeof trace === "string") {
    return trace.slice(0, 8000);
  }

  if (!Array.isArray(trace)) {
    try {
      return JSON.stringify(trace, null, 2).slice(0, 8000);
    } catch (e) {
      return "";
    }
  }

  return trace
    .slice(0, 40)
    .map((frame, index) => {
      if (typeof frame === "string") {
        return `#${index} ${frame}`;
      }

      const location = [frame?.file, frame?.line].filter(Boolean).join(":");
      const call = [frame?.class, frame?.type, frame?.function].filter(Boolean).join("");

      return `#${index} ${location}${call ? ` ${call}()` : ""}`;
    })
    .join("\n")
    .slice(0, 8000);
}

function normalizeResponseBody(body) {
  if (!body) {
    return "";
  }

  if (typeof body === "string") {
    return body.slice(0, 8000);
  }

  try {
    return JSON.stringify(body, null, 2).slice(0, 8000);
  } catch (e) {
    return "";
  }
}

function normalizeStats(stats) {
  const source = stats && typeof stats === "object" ? stats : {};

  const read = (keys) => {
    for (const key of keys) {
      const value = source[key];

      if (value !== undefined && value !== null && value !== "") {
        return Number(value) || 0;
      }
    }

    return 0;
  };

  return {
    fetched_count: read(["fetched_count", "fetched", "total_fetched"]),
    processed_count: read(["processed_count", "processed"]),
    skipped_count: read(["skipped_count", "skipped"]),
    inserted_count: read(["inserted_count", "inserted", "created_count"]),
    updated_count: read(["updated_count", "updated"]),
    untouched_count: read(["untouched_count", "untouched", "unchanged_count"]),
  };
}

/**
 * A backend többféle mezőnevet használhat ugyanarra az adatra.
 * Az első nem üres találatot adjuk vissza.
 */
function pick(source, keys, fallback = "") {
  for (const key of keys) {
    const value = key.split(".").reduce((acc, part) => acc?.[part], source);

    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return fallback;
}

function normalizeEntry(entry) {
  if (!entry || typeof entry !== "object") {
    return null;
  }

  const stats = normalizeStats(entry.stats || entry);
  const rawStatus = String(pick(entry, ["status", "result", "state"], "")).toLowerCase();

  const isOprit = rawStatus === ERP_SYNC_LOG_STATUS.OPRIT;

  // Hibás a bejegyzés, ha explicit így jelölt, vagy ha van hibaüzenet/kivétel.
  const hasErrorPayload = Boolean(
    pick(entry, [
      "error_message",
      "error",
      "exception",
      "error_exception",
      "trace",
      "error_trace",
    ], "")
  );
  const isRunning = rawStatus === ERP_SYNC_LOG_STATUS.RUNNING;
  const isError =
    rawStatus === ERP_SYNC_LOG_STATUS.ERROR ||
    rawStatus === "failed" ||
    rawStatus === "failure" ||
    entry.success === false ||
    (!rawStatus && hasErrorPayload);

  const normalizedStatus = isOprit
    ? ERP_SYNC_LOG_STATUS.OPRIT
    : isError
      ? ERP_SYNC_LOG_STATUS.ERROR
      : isRunning
        ? ERP_SYNC_LOG_STATUS.RUNNING
        : ERP_SYNC_LOG_STATUS.SUCCESS;

  const line = pick(entry, ["error_line", "line"], null);
  const status = pick(entry, ["error_status", "response_status", "http_status", "status_code"], null);
  const previous = String(pick(entry, ["error_previous", "previous"], ""));

  return {
    id:
      pick(entry, ["id", "uuid"], "") ||
      `${entry.entity_code || ""}-${entry.created_at || Date.now()}`,
    entity_code: pick(entry, ["entity_code", "code", "entity"], ""),
    entity_name: pick(entry, ["entity_name", "name"], "") || entry.entity_code || "",
    status: normalizedStatus,
    duration_ms: Number(pick(entry, ["duration_ms", "duration", "elapsed_ms"], 0)) || 0,
    error_message: String(
      pick(entry, ["error_message", "error", "message", "exception_message"], "")
    ),
    error_status: status !== null && status !== "" ? Number(status) : null,
    error_exception: String(pick(entry, ["error_exception", "exception", "exception_class"], "")),
    error_file: String(pick(entry, ["error_file", "file"], "")),
    error_line: line !== null && line !== "" ? Number(line) : null,
    error_previous: previous,
    error_trace: normalizeTrace(pick(entry, ["error_trace", "trace", "stack_trace", "stack"], "")),
    request_url: String(pick(entry, ["request_url", "url", "endpoint"], "")),
    request_method: String(pick(entry, ["request_method", "method"], "")).toUpperCase(),
    response_body: normalizeResponseBody(
      pick(entry, ["response_body", "response", "payload", "context"], "")
    ),
    created_at:
      pick(entry, ["created_at", "createdAt", "logged_at", "timestamp"], "") ||
      new Date().toISOString(),
    is_local: Boolean(entry.is_local),
    ...stats,
    changed_count: stats.inserted_count + stats.updated_count,
    // A nyers rekord megmarad, hogy ismeretlen mezőszerkezet is diagnosztizálható legyen.
    raw_entry: entry.is_local ? null : entry,
  };
}

function unwrapList(response) {
  const payload = response?.data;

  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;

  return [];
}

export function buildSyncLogEntry({
  entityCode,
  entityName,
  status,
  stats,
  errorMessage,
  errorDetails,
  durationMs,
}) {
  const details = errorDetails && typeof errorDetails === "object" ? errorDetails : {};

  return {
    entity_code: entityCode || "",
    entity_name: entityName || entityCode || "",
    status:
      status === ERP_SYNC_LOG_STATUS.ERROR
        ? ERP_SYNC_LOG_STATUS.ERROR
        : ERP_SYNC_LOG_STATUS.SUCCESS,
    duration_ms: Number(durationMs || 0),
    error_message: errorMessage ? String(errorMessage).slice(0, 8000) : "",
    error_status: details.status || null,
    error_exception: details.serverException || "",
    error_file: details.serverFile || "",
    error_line: details.serverLine || null,
    error_trace: details.serverTrace || "",
    request_url: details.requestUrl || "",
    request_method: details.requestMethod || "",
    response_body: details.responseBody || "",
    stats: normalizeStats(stats),
    created_at: new Date().toISOString(),
  };
}

function storeLocally(entry) {
  const items = readFallback();
  items.unshift(
    normalizeEntry({
      ...entry,
      ...normalizeStats(entry.stats),
      id: `local-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      is_local: true,
    })
  );
  writeFallback(items);
}

/**
 * Diagnosztika: az utolsó nyers szerver válasz és a leképezés eredménye.
 * Ezzel derül ki, ha a backend más mezőneveket használ, mint amit várunk.
 */
let lastRawResponse = null;

export function getLastRawResponse() {
  return lastRawResponse;
}

/**
 * Napló bejegyzés mentése. Sosem dob hibát.
 *
 * A szerver a szinkron végén maga naplóz (siker és hiba esetén egyaránt),
 * ezért a kliens csak akkor tárol helyben, ha a kérés el sem jutott a
 * szerverig (hálózati hiba, timeout) — ilyenkor ugyanis nincs szerver oldali
 * bejegyzés sem, és enélkül nyomtalanul elveszne az esemény.
 */
export async function saveSyncLog(entry) {
  const payload = buildSyncLogEntry(entry);

  if (payload.status !== ERP_SYNC_LOG_STATUS.ERROR) {
    return true;
  }

  if (reachedServer(entry?.error)) {
    return true;
  }

  storeLocally(payload);
  return false;
}

/**
 * Igaz, ha a szerver válaszolt a kérésre — ekkor a szerver oldali napló
 * tartalmazza az esemény hiteles részleteit.
 */
function reachedServer(error) {
  return Boolean(error?.response?.status);
}

/**
 * Napló bejegyzések lekérése. Sosem dob hibát, mindig tömböt ad vissza.
 *
 * Nomenklatúra nézetből entitáskódra szűrve kérjük a szervertől a naplókat,
 * és a választ kliensoldalon is utószűrjük, hogy régebbi backend mellett se
 * keveredjenek a különböző típusok bejegyzései.
 */
export async function fetchSyncLogs(params = {}) {
  const entityCode = normalizeEntityCode(params.entityCode || params.entity_code);
  const localItems = filterByEntityCode(
    readFallback().map(normalizeEntry).filter(Boolean),
    entityCode
  );

  if (readAvailable) {
    try {
      const response = await axios.get(endpoints.ERP_SYNC_LOGS, {
        params: {
          limit: params.limit || 50,
          ...(entityCode ? { entity_code: entityCode } : {}),
        },
      });

      const rawList = unwrapList(response);
      const envelope = response?.data?.data;
      lastRawResponse = {
        fetchedAt: new Date().toISOString(),
        requestedEntityCode: entityCode || null,
        count: rawList.length,
        // Az első rekord nyers szerkezete megmutatja a tényleges mezőneveket.
        sample: rawList.length ? rawList[0] : null,
        envelopeKeys:
          response?.data && typeof response.data === "object"
            ? Object.keys(response.data)
            : [],
        // A szerver jelzi, hogy a napló tábla létezik-e az adott cég
        // adatbázisában. Hiányzó migráció esetén ez azonnal kiderül.
        storage:
          envelope && typeof envelope === "object" ? envelope.storage || null : null,
      };

      const remoteItems = filterByEntityCode(
        rawList.map(normalizeEntry).filter(Boolean),
        entityCode
      );

      return [...remoteItems, ...localItems].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
    } catch (e) {
      lastRawResponse = {
        fetchedAt: new Date().toISOString(),
        requestedEntityCode: entityCode || null,
        error: e?.response?.status
          ? `HTTP ${e.response.status}`
          : e?.message || "unknown error",
        body: e?.response?.data || null,
      };

      if (isMissingReadEndpoint(e)) {
        readAvailable = false;
      }
    }
  }

  return localItems.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
}

/**
 * A szerver oldali napló olvasható-e. Ez határozza meg, hogy a UI
 * "csak helyi bejegyzések" figyelmeztetést mutat-e.
 */
export function isRemoteLoggingAvailable() {
  return readAvailable;
}

/**
 * A "nincs végpont" jelzés visszavonása, hogy egyetlen sikertelen kérés
 * ne tiltsa le véglegesen a szerver oldali olvasást a munkamenetben.
 */
export function resetRemoteLoggingAvailability() {
  readAvailable = true;
}

export async function cancelSyncLog(uuid) {
  const url = endpoints.replaceParams(endpoints.ERP_SYNC_LOG_CANCEL, { uuid });
  const response = await axios.post(url);
  return response?.data?.data || response?.data || null;
}

/**
 * A kliens csak akkor tárol helyben, ha a kérés el sem jutott a szerverig.
 * A szerver oldali naplózás mindig aktív, ezért ez mindig false.
 */
export function isRemoteWriteAvailable() {
  return false;
}

/**
 * A helyi (localStorage) tartalék bejegyzések törlése. Ezek csak akkor
 * keletkeznek, ha a kérés el sem jutott a szerverig, és könnyen összekeverhetők
 * a szerver hiteles naplójával.
 */
export function clearLocalSyncLogs() {
  writeFallback([]);
}
